import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar, Footer } from "../../components/Layout";
import { ArrowRight, CheckCircle2, Star, BookOpen, Sparkles, Volume2, Brain, Zap } from "lucide-react";

/**
 * SeoLanding — reusable template for SEO landing pages.
 * Each instance targets a single primary keyword (e.g. "cours de japonais").
 *
 * Props:
 *  - testid: unique data-testid root (e.g. "seo-cours")
 *  - h1: main visible H1, exact match keyword
 *  - subtitle: short tagline under H1
 *  - lead: 2-3 sentence intro (~50-60 words, mentions keyword 2-3 times)
 *  - sections: array of { h2, paragraphs: [string], bullets?: [string] }
 *  - faq: array of { q, a } for FAQ Schema
 *  - canonical: full canonical URL
 *  - title: page <title>
 *  - description: meta description (150-160 chars)
 *  - keywords: comma-separated keywords for the meta tag
 */
const SeoLanding = ({
  testid, h1, subtitle, lead, sections, faq,
  canonical, title, description, keywords,
}) => {
  // SPA: rewrite head tags client-side so Googlebot sees them after JS execution.
  // (For real first-paint SEO, this would need SSR — but Google does execute JS now.)
  useEffect(() => {
    const prev = {
      title: document.title,
      desc: document.querySelector('meta[name="description"]')?.getAttribute("content"),
      keywords: document.querySelector('meta[name="keywords"]')?.getAttribute("content"),
      canonical: document.querySelector('link[rel="canonical"]')?.getAttribute("href"),
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute("content"),
      ogDesc: document.querySelector('meta[property="og:description"]')?.getAttribute("content"),
      ogUrl: document.querySelector('meta[property="og:url"]')?.getAttribute("content"),
    };
    document.title = title;
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el && value !== undefined) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="keywords"]', "content", keywords);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", canonical);

    // Note: FAQ JSON-LD is rendered directly in the JSX (see below) so that it's
    // present in the DOM at first render and Googlebot can always read it. We
    // intentionally do NOT inject it again here to avoid duplicate FAQPage entries.

    return () => {
      // restore
      document.title = prev.title;
      setMeta('meta[name="description"]', "content", prev.desc);
      setMeta('meta[name="keywords"]', "content", prev.keywords);
      setMeta('link[rel="canonical"]', "href", prev.canonical);
      setMeta('meta[property="og:title"]', "content", prev.ogTitle);
      setMeta('meta[property="og:description"]', "content", prev.ogDesc);
      setMeta('meta[property="og:url"]', "content", prev.ogUrl);
    };
  }, [title, description, keywords, canonical, faq]);

  // Pre-build the FAQ JSON-LD once so it's serialized into the DOM at first render
  // (in addition to the useEffect-driven <head> injection). This belt-and-braces
  // approach ensures Googlebot always sees the schema, even if JS execution is
  // partial. Both copies carry the same id, so document.head injection removes
  // duplicates first.
  const faqJsonLd = faq?.length
    ? JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: String(f.q || "").trim(),
          acceptedAnswer: {
            "@type": "Answer",
            text: String(f.a || "").trim(),
          },
        })),
      })
    : null;

  return (
    <div className="washi-bg min-h-screen" data-testid={testid}>
      {faqJsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: faqJsonLd }}
        />
      )}
      <Navbar />

      {/* HERO */}
      <header className="max-w-5xl mx-auto px-6 sm:px-12 pt-16 pb-12">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1.5 rounded-full border border-[#1A2A40]/15 text-xs font-bold tracking-widest text-[#E94A3F] mb-5">
          <Sparkles className="w-3.5 h-3.5" /> ESSAI GRATUIT 3 JOURS · SANS CB
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A2A40] leading-[1.05] mb-4">
          {h1}
        </h1>
        <p className="text-lg sm:text-xl text-[#4A5568] max-w-2xl mb-6">{subtitle}</p>
        <p className="text-base text-[#4A5568] leading-relaxed max-w-3xl mb-8">{lead}</p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/register"
            data-testid={`${testid}-cta-primary`}
            className="bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-3.5 px-6 rounded-full shadow-brut inline-flex items-center gap-2"
          >
            Commencer gratuitement <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/vocabulaire"
            data-testid={`${testid}-cta-secondary`}
            className="bg-white hover:bg-[#FFF5E0] text-[#1A2A40] font-bold py-3.5 px-6 rounded-full border-2 border-[#1A2A40] inline-flex items-center gap-2"
          >
            Voir le vocabulaire gratuit
          </Link>
        </div>
      </header>

      {/* QUICK STATS */}
      <section className="max-w-5xl mx-auto px-6 sm:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          { k: "200", v: "leçons N5 → N3", icon: BookOpen },
          { k: "636", v: "mots de vocabulaire", icon: Brain },
          { k: "15 min", v: "par jour", icon: Zap },
          { k: "🎙️", v: "audio natif Atsuko & Alexis", icon: Volume2 },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border-2 border-[#1A2A40]/10 p-5 text-center">
            <div className="font-display font-black text-3xl text-[#E94A3F] mb-1">{s.k}</div>
            <div className="text-xs text-[#4A5568] uppercase tracking-wider">{s.v}</div>
          </div>
        ))}
      </section>

      {/* SECTIONS */}
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12 space-y-12">
        {sections.map((sec, i) => (
          <section key={i} data-testid={`${testid}-section-${i}`}>
            <h2 className="font-display font-black text-3xl text-[#1A2A40] mb-4 leading-tight">{sec.h2}</h2>
            {sec.paragraphs.map((p, j) => (
              <p key={j} className="text-[#4A5568] leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }} />
            ))}
            {sec.bullets && (
              <ul className="text-[#4A5568] leading-relaxed mb-4 space-y-2 pl-5 list-disc">
                {sec.bullets.map((b, k) => (
                  <li key={k} dangerouslySetInnerHTML={{ __html: b }} />
                ))}
              </ul>
            )}
          </section>
        ))}
      </main>

      {/* FAQ */}
      {faq?.length > 0 && (
        <section className="bg-[#F3F6F1] py-16" data-testid={`${testid}-faq`}>
          <div className="max-w-3xl mx-auto px-6 sm:px-10">
            <h2 className="font-display font-black text-3xl text-[#1A2A40] mb-2 leading-tight">Questions fréquentes</h2>
            <p className="text-sm text-[#A0AEC0] mb-8">Tout ce que tu te demandes avant de commencer.</p>
            <div className="space-y-4">
              {faq.map((f, i) => (
                <details key={i} className="bg-white rounded-xl border border-[#1A2A40]/10 p-5 group" data-testid={`${testid}-faq-${i}`}>
                  <summary className="cursor-pointer font-bold text-[#1A2A40] flex items-center justify-between">
                    <span>{f.q}</span>
                    <span className="text-[#E94A3F] text-2xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-[#4A5568] leading-relaxed" dangerouslySetInnerHTML={{ __html: f.a }} />
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="max-w-3xl mx-auto px-6 sm:px-12 py-20 text-center" data-testid={`${testid}-final-cta`}>
        <h2 className="font-display font-black text-3xl sm:text-4xl text-[#1A2A40] mb-4 leading-tight">
          Prêt à <span className="text-[#E94A3F]">commencer</span> ?
        </h2>
        <p className="text-[#4A5568] mb-8 max-w-xl mx-auto">
          Essai gratuit 3 jours. Sans carte bancaire. Sans engagement.
          Si la méthode ne te convient pas, tu pars sans dire au revoir.
        </p>
        <Link
          to="/register"
          data-testid={`${testid}-final-cta-button`}
          className="bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-4 px-8 rounded-full shadow-brut inline-flex items-center gap-2 text-lg"
        >
          Démarrer ma première leçon <ArrowRight className="w-5 h-5" />
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default SeoLanding;
